import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import {
  Loader2,
  FileText,
  Download,
  Plus,
  Filter,
  X,
  Eye,
  Trash2,
  RefreshCw,
  FileSpreadsheet
} from 'lucide-react';
import { exportGSTBillsToExcel } from '../utils/excelExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

function GSTBills() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [gstBills, setGstBills] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regularBills, setRegularBills] = useState([]);
  const [theaters, setTheaters] = useState([]);
  
  // Filters
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterCustomerName, setFilterCustomerName] = useState('');
  
  // Generate GST Bill Dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateMode, setGenerateMode] = useState('from_bills'); // 'from_bills' or 'manual'
  const [generating, setGenerating] = useState(false);
  
  // Generate from bills
  const [selectedBills, setSelectedBills] = useState([]);
  const [billFilters, setBillFilters] = useState({
    branch_id: '',
    theater_id: '',
    start_date: '',
    end_date: ''
  });
  
  // Customer details for GST bill
  const [customerDetails, setCustomerDetails] = useState({
    gst_bill_number: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_state: '',
    customer_gstin: '',
    bill_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Manual GST bill items
  const [manualItems, setManualItems] = useState([]);
  
  // View details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedGSTBill, setSelectedGSTBill] = useState(null);

  useEffect(() => {
    fetchBranches();
    fetchTheaters();
    fetchGSTBills();
  }, []);

  useEffect(() => {
    if (filterBranch || filterStartDate || filterEndDate || filterCustomerName) {
      fetchGSTBills();
    }
  }, [filterBranch, filterStartDate, filterEndDate, filterCustomerName]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchTheaters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/theaters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTheaters(data);
      }
    } catch (error) {
      console.error('Error fetching theaters:', error);
    }
  };

  const fetchGSTBills = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterBranch) params.append('branch_id', filterBranch);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterCustomerName) params.append('customer_name', filterCustomerName);
      
      const response = await fetch(`${BACKEND_URL}/api/gst-bills?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGstBills(data);
      }
    } catch (error) {
      console.error('Error fetching GST bills:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch GST bills',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRegularBills = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (billFilters.branch_id) params.append('branch_id', billFilters.branch_id);
      if (billFilters.theater_id) params.append('theater_id', billFilters.theater_id);
      if (billFilters.start_date) params.append('start_date', billFilters.start_date);
      if (billFilters.end_date) params.append('end_date', billFilters.end_date);
      
      const response = await fetch(`${BACKEND_URL}/api/bills?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRegularBills(data);
      }
    } catch (error) {
      console.error('Error fetching regular bills:', error);
    }
  };

  const handleOpenGenerateDialog = (mode) => {
    setGenerateMode(mode);
    setGenerateDialogOpen(true);
    setSelectedBills([]);
    setCustomerDetails({
      gst_bill_number: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_state: '',
      customer_gstin: '',
      bill_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setManualItems([]);
    if (mode === 'from_bills') {
      fetchRegularBills();
    }
  };

  const handleToggleBillSelection = (billId) => {
    setSelectedBills(prev => {
      if (prev.includes(billId)) {
        return prev.filter(id => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  };

  const handleGenerateGSTBill = async () => {
    if (generateMode === 'from_bills' && selectedBills.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one bill',
        variant: 'destructive'
      });
      return;
    }

    if (generateMode === 'manual' && manualItems.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one item',
        variant: 'destructive'
      });
      return;
    }

    if (!customerDetails.customer_name || !customerDetails.customer_phone) {
      toast({
        title: 'Error',
        description: 'Customer name and phone are required',
        variant: 'destructive'
      });
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = generateMode === 'from_bills' 
        ? `${BACKEND_URL}/api/gst-bills/generate`
        : `${BACKEND_URL}/api/gst-bills/manual`;
      
      const payload = {
        ...customerDetails,
        regular_bill_ids: generateMode === 'from_bills' ? selectedBills : [],
        items: generateMode === 'manual' ? manualItems : []
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'GST bill generated successfully'
        });
        setGenerateDialogOpen(false);
        fetchGSTBills();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate GST bill');
      }
    } catch (error) {
      console.error('Error generating GST bill:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate GST bill',
        variant: 'destructive'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async (billId, billNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/gst-bills/${billId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `GST_Bill_${billNumber.replace(/\//g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: 'Success',
          description: 'GST bill downloaded successfully'
        });
      } else {
        throw new Error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to download GST bill',
        variant: 'destructive'
      });
    }
  };

  const handleViewDetails = (bill) => {
    setSelectedGSTBill(bill);
    setViewDialogOpen(true);
  };

  const handleDeleteGSTBill = async (billId) => {
    if (!window.confirm('Are you sure you want to delete this GST bill?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/gst-bills/${billId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'GST bill deleted successfully'
        });
        fetchGSTBills();
      } else {
        throw new Error('Failed to delete GST bill');
      }
    } catch (error) {
      console.error('Error deleting GST bill:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete GST bill',
        variant: 'destructive'
      });
    }
  };

  const handleExportToExcel = () => {
    try {
      if (gstBills.length === 0) {
        toast({
          title: 'No Data',
          description: 'No GST bills available to export',
          variant: 'destructive'
        });
        return;
      }

      // Create filename with date range if filters are applied
      let filename = 'GST_Bills_Export';
      if (filterStartDate && filterEndDate) {
        filename = `GST_Bills_${filterStartDate}_to_${filterEndDate}`;
      } else if (filterBranch) {
        const branchName = branches.find(b => b.id === filterBranch)?.name || 'Branch';
        filename = `GST_Bills_${branchName}`;
      }

      exportGSTBillsToExcel(gstBills, filename);
      
      toast({
        title: 'Success',
        description: `Exported ${gstBills.length} GST bills to Excel`
      });
    } catch (error) {
      console.error('Error exporting GST bills:', error);
      toast({
        title: 'Error',
        description: 'Failed to export GST bills',
        variant: 'destructive'
      });
    }
  };


  const addManualItem = () => {
    setManualItems([...manualItems, {
      item_type: 'miscellaneous',
      item_name: '',
      quantity: 1,
      rate: 0
    }]);
  };

  const removeManualItem = (index) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  const updateManualItem = (index, field, value) => {
    const updated = [...manualItems];
    updated[index] = { ...updated[index], [field]: value };
    setManualItems(updated);
  };

  const clearFilters = () => {
    setFilterBranch('');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterCustomerName('');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-8 w-8 text-blue-600" />
          GST Bills
        </h1>
        <p className="text-gray-500 mt-2">Generate and manage GST tax invoices</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {user?.role === 'admin' && (
              <div>
                <Label htmlFor="filter-branch">Branch</Label>
                <Select value={filterBranch || "all"} onValueChange={(value) => setFilterBranch(value === "all" ? "" : value)}>
                  <SelectTrigger id="filter-branch">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label htmlFor="filter-start-date">Start Date</Label>
              <Input
                id="filter-start-date"
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="filter-end-date">End Date</Label>
              <Input
                id="filter-end-date"
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="filter-customer">Customer Name</Label>
              <Input
                id="filter-customer"
                type="text"
                value={filterCustomerName}
                onChange={(e) => setFilterCustomerName(e.target.value)}
                placeholder="Search by name"
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchGSTBills} size="sm" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={clearFilters} size="sm" variant="outline">
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <Button onClick={() => handleOpenGenerateDialog('from_bills')}>
          <Plus className="h-4 w-4 mr-2" />
          Generate from Bills
        </Button>
        <Button onClick={() => handleOpenGenerateDialog('manual')} variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Create Manual GST Bill
        </Button>
        <Button 
          onClick={handleExportToExcel} 
          variant="outline"
          disabled={gstBills.length === 0}
          className="ml-auto"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export to Excel ({gstBills.length} bills)
        </Button>
      </div>

      {/* GST Bills List */}
      <Card>
        <CardHeader>
          <CardTitle>Generated GST Bills ({gstBills.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : gstBills.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No GST bills found. Generate your first GST bill!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {gstBills.map((bill) => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">{bill.gst_bill_number}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{bill.bill_date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>{bill.customer_name}</div>
                        <div className="text-xs text-gray-500">{bill.customer_phone}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{bill.branch_name}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                        ₹{bill.grand_total.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          bill.is_interstate 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {bill.is_interstate ? 'Interstate (IGST)' : 'Intrastate (CGST+SGST)'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(bill)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPDF(bill.id, bill.gst_bill_number)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteGSTBill(bill.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate GST Bill Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {generateMode === 'from_bills' ? 'Generate GST Bill from Regular Bills' : 'Create Manual GST Bill'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Customer Details */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-3">Bill Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="gst_bill_number">GST Bill Number (Optional)</Label>
                  <Input
                    id="gst_bill_number"
                    value={customerDetails.gst_bill_number}
                    onChange={(e) => setCustomerDetails({...customerDetails, gst_bill_number: e.target.value})}
                    placeholder="Leave empty for auto-generation or enter custom number"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If left empty, system will auto-generate in format: GST/BRANCH_CODE/YEAR/SEQUENCE
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={customerDetails.customer_name}
                    onChange={(e) => setCustomerDetails({...customerDetails, customer_name: e.target.value})}
                    placeholder="Enter customer name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_phone">Phone *</Label>
                  <Input
                    id="customer_phone"
                    value={customerDetails.customer_phone}
                    onChange={(e) => setCustomerDetails({...customerDetails, customer_phone: e.target.value})}
                    placeholder="10-digit phone number"
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_email">Email</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={customerDetails.customer_email}
                    onChange={(e) => setCustomerDetails({...customerDetails, customer_email: e.target.value})}
                    placeholder="customer@example.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="customer_state">State</Label>
                  <Select
                    value={customerDetails.customer_state}
                    onValueChange={(value) => setCustomerDetails({...customerDetails, customer_state: value})}
                  >
                    <SelectTrigger id="customer_state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="customer_gstin">Customer GSTIN (Optional)</Label>
                  <Input
                    id="customer_gstin"
                    value={customerDetails.customer_gstin}
                    onChange={(e) => setCustomerDetails({...customerDetails, customer_gstin: e.target.value})}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
                
                <div>
                  <Label htmlFor="bill_date">Bill Date *</Label>
                  <Input
                    id="bill_date"
                    type="date"
                    value={customerDetails.bill_date}
                    onChange={(e) => setCustomerDetails({...customerDetails, bill_date: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={customerDetails.notes}
                  onChange={(e) => setCustomerDetails({...customerDetails, notes: e.target.value})}
                  placeholder="Additional notes for this GST bill"
                />
              </div>
            </div>

            {/* Bill Selection or Manual Items */}
            {generateMode === 'from_bills' ? (
              <div>
                <h3 className="font-semibold mb-3">Select Bills to Include</h3>
                
                {/* Bill Filters */}
                <div className="grid grid-cols-4 gap-3 mb-4 p-3 bg-gray-50 rounded">
                  <Select
                    value={billFilters.branch_id || "all"}
                    onValueChange={(value) => setBillFilters({...billFilters, branch_id: value === "all" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={billFilters.theater_id || "all"}
                    onValueChange={(value) => setBillFilters({...billFilters, theater_id: value === "all" ? "" : value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by theater" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Theaters</SelectItem>
                      {theaters.map(theater => (
                        <SelectItem key={theater.id} value={theater.id}>{theater.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Input
                    type="date"
                    value={billFilters.start_date}
                    onChange={(e) => setBillFilters({...billFilters, start_date: e.target.value})}
                    placeholder="Start date"
                  />
                  
                  <Input
                    type="date"
                    value={billFilters.end_date}
                    onChange={(e) => setBillFilters({...billFilters, end_date: e.target.value})}
                    placeholder="End date"
                  />
                  
                  <Button onClick={fetchRegularBills} size="sm" className="col-span-4">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Load Bills
                  </Button>
                </div>
                
                {/* Bills List */}
                <div className="max-h-64 overflow-y-auto border rounded p-2">
                  {regularBills.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No bills found. Adjust filters and click Load Bills.</p>
                  ) : (
                    regularBills.map(bill => (
                      <div key={bill.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                        <Checkbox
                          checked={selectedBills.includes(bill.id)}
                          onCheckedChange={() => handleToggleBillSelection(bill.id)}
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {bill.theater_name} - {bill.time_slot} ({bill.date})
                          </div>
                          <div className="text-xs text-gray-500">
                            {bill.branch_name} | ₹{bill.actual_amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mt-2">
                  Selected: {selectedBills.length} bill(s)
                </p>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Manual Items</h3>
                  <Button onClick={addManualItem} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {manualItems.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No items added. Click "Add Item" to begin.</p>
                  ) : (
                    manualItems.map((item, index) => (
                      <div key={index} className="grid grid-cols-6 gap-2 p-2 border rounded items-end">
                        <div>
                          <Label>Type</Label>
                          <Select
                            value={item.item_type}
                            onValueChange={(value) => updateManualItem(index, 'item_type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="theater">Theater</SelectItem>
                              <SelectItem value="cake">Cake</SelectItem>
                              <SelectItem value="decoration">Decoration</SelectItem>
                              <SelectItem value="food">Food</SelectItem>
                              <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="col-span-2">
                          <Label>Item Name</Label>
                          <Input
                            value={item.item_name}
                            onChange={(e) => updateManualItem(index, 'item_name', e.target.value)}
                            placeholder="Item name"
                          />
                        </div>
                        
                        <div>
                          <Label>Qty</Label>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateManualItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        
                        <div>
                          <Label>Rate (₹)</Label>
                          <Input
                            type="number"
                            value={item.rate}
                            onChange={(e) => updateManualItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeManualItem(index)}
                          className="text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateGSTBill} disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate GST Bill'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>GST Bill Details</DialogTitle>
          </DialogHeader>
          
          {selectedGSTBill && (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                <div>
                  <p className="text-sm text-gray-600">Bill Number</p>
                  <p className="font-semibold">{selectedGSTBill.gst_bill_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-semibold">{selectedGSTBill.bill_date}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Branch</p>
                  <p className="font-semibold">{selectedGSTBill.branch_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Branch GSTIN</p>
                  <p className="font-semibold">{selectedGSTBill.branch_gstin}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Customer Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600">Name:</span> {selectedGSTBill.customer_name}</div>
                  <div><span className="text-gray-600">Phone:</span> {selectedGSTBill.customer_phone}</div>
                  {selectedGSTBill.customer_email && (
                    <div><span className="text-gray-600">Email:</span> {selectedGSTBill.customer_email}</div>
                  )}
                  {selectedGSTBill.billed_by && (
                    <div><span className="text-gray-600">Billed By:</span> {selectedGSTBill.billed_by}</div>
                  )}
                  {selectedGSTBill.customer_state && (
                    <div><span className="text-gray-600">State:</span> {selectedGSTBill.customer_state}</div>
                  )}
                  {selectedGSTBill.customer_gstin && (
                    <div><span className="text-gray-600">GSTIN:</span> {selectedGSTBill.customer_gstin}</div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Items</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-2 py-2 text-left">Item</th>
                        <th className="px-2 py-2 text-center">Qty</th>
                        <th className="px-2 py-2 text-right">Rate</th>
                        <th className="px-2 py-2 text-right">Taxable</th>
                        <th className="px-2 py-2 text-center">GST%</th>
                        {selectedGSTBill.is_interstate ? (
                          <th className="px-2 py-2 text-right">IGST</th>
                        ) : (
                          <>
                            <th className="px-2 py-2 text-right">CGST</th>
                            <th className="px-2 py-2 text-right">SGST</th>
                          </>
                        )}
                        <th className="px-2 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGSTBill.items.map((item, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="px-2 py-2">{item.item_name}</td>
                          <td className="px-2 py-2 text-center">{item.quantity}</td>
                          <td className="px-2 py-2 text-right">₹{item.rate.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right">₹{item.taxable_amount.toFixed(2)}</td>
                          <td className="px-2 py-2 text-center">{item.gst_rate}%</td>
                          {selectedGSTBill.is_interstate ? (
                            <td className="px-2 py-2 text-right">₹{item.igst_amount?.toFixed(2) || '0.00'}</td>
                          ) : (
                            <>
                              <td className="px-2 py-2 text-right">₹{item.cgst_amount?.toFixed(2) || '0.00'}</td>
                              <td className="px-2 py-2 text-right">₹{item.sgst_amount?.toFixed(2) || '0.00'}</td>
                            </>
                          )}
                          <td className="px-2 py-2 text-right font-semibold">₹{item.total_amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Summary */}
              <div className="border-t pt-4">
                <div className="space-y-2 text-right">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Taxable Amount:</span>
                    <span className="font-semibold">₹{selectedGSTBill.total_taxable_amount.toFixed(2)}</span>
                  </div>
                  {selectedGSTBill.is_interstate ? (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total IGST:</span>
                      <span className="font-semibold">₹{selectedGSTBill.total_igst.toFixed(2)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total CGST:</span>
                        <span className="font-semibold">₹{selectedGSTBill.total_cgst.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total SGST:</span>
                        <span className="font-semibold">₹{selectedGSTBill.total_sgst.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between text-lg border-t pt-2">
                    <span className="font-semibold">Grand Total:</span>
                    <span className="font-bold text-blue-600">₹{selectedGSTBill.grand_total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedGSTBill.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600">Notes:</p>
                  <p className="text-sm">{selectedGSTBill.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
            {selectedGSTBill && (
              <Button onClick={() => handleDownloadPDF(selectedGSTBill.id, selectedGSTBill.gst_bill_number)}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default GSTBills;
